/**
 * Browser-side error reporting.
 *
 * Anything that breaks in a visitor's browser is invisible to the webmaster
 * otherwise — this forwards it to the PHP log so it shows up in the Dev logs
 * tab alongside server errors.
 *
 * Deliberately conservative: identical messages are only sent once per page
 * load, there is a hard cap per session, and a failure to report is swallowed
 * silently. Reporting must never make a bad page worse.
 */

import { logs } from './api';

const MAX_REPORTS_PER_SESSION = 10;

let installed = false;
let sent = 0;
let reporting = false;
const seen = {};

function report(level, message, meta) {
  if (!message || sent >= MAX_REPORTS_PER_SESSION) return;

  const key = level + '|' + message;
  if (seen[key]) return;
  seen[key] = true;

  // Guards against an error thrown inside reporting triggering another report.
  if (reporting) return;
  reporting = true;
  sent += 1;

  const payload = Object.assign({ url: window.location.pathname }, meta || {});

  logs.ingest(level, message, payload).catch(() => {}).then(
    () => {
      reporting = false;
    },
    () => {
      reporting = false;
    }
  );
}

export function installDevLogging() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (event) => {
    // Failed <img>/<script> loads surface here with no `error` object; a broken
    // gallery image is worth knowing about, so report it as a warning.
    if (event.target && event.target !== window && event.target.src) {
      report('warn', 'Failed to load resource', { src: String(event.target.src).slice(0, 300) });
      return;
    }

    const error = event.error;
    report('error', event.message || 'Unhandled error', {
      file: event.filename,
      line: event.lineno,
      column: event.colno,
      stack: error && error.stack ? String(error.stack).slice(0, 800) : undefined,
    });
  }, true);

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const message =
      reason && reason.message ? reason.message : typeof reason === 'string' ? reason : 'Unhandled promise rejection';
    report('error', message, {
      stack: reason && reason.stack ? String(reason.stack).slice(0, 800) : undefined,
    });
  });
}

/** Manual reporting hook for error boundaries. */
export function reportError(message, meta) {
  report('error', message, meta);
}
